import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  obtenerFacturacionPorPeriodo,
  exportarReporteFacturacion,
  getFarmacias,
} from "../../../slices/thunks";
import { reset_solicitud_flag } from "../../../slices/configuracion/solicitudes/reducer";
import { getUrlPrefirmada } from "../../../helpers/backend_helper";
import TableContainerNoPagination from "../../../Components/Common/TableContainerNoPagination";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  Input,
  Button,
  Label,
  UncontrolledTooltip,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import FileViewer from "../../../Components/Common/FileViewer";
import { DatosSesion } from "../../../Components/Common/DatosSesion";

// Opciones para los selectores (mantenemos las mismas)
const opcionesTipo = [
  { value: "Todas", label: "Todas" },
  { value: "Aguda", label: "Aguda" },
  { value: "Cronica", label: "Crónica" },
];

const opcionesEstatusPago = [
  { value: "Todos", label: "Todos" },
  { value: "Pagada", label: "Pagada" },
  { value: "Por Pagar", label: "Por Pagar" },
  { value: "Pendiente", label: "Pendiente" },
  { value: "Rechazada", label: "Rechazada" },
  { value: "Parcial", label: "Parcial" },
];

const opcionesAseguradora = [
  { value: "Todas", label: "Todas las aseguradoras" },
  { value: "MyH+", label: "MyH+" },
  { value: "Seguros Mercantil", label: "Seguros Mercantil" },
  { value: "LATINA SALUD", label: "Latina Salud" },
  { value: "HISPANA DE SEGUROS", label: "Hispana de Seguros" },
  { value: "REAL SEGUROS", label: "Real Seguros" },
];

/* const opcionesEstatus = [
  { value: "", label: "Todos los estados" },
  { value: "Nueva Solicitud", label: "Nueva Solicitud" },
  { value: "Por Entregar", label: "Por Entregar" },
  { value: "En Transito", label: "En Tránsito" },
  { value: "Entregado", label: "Entregado" },
  { value: "Cancelado",  label: "Cancelado" },
  { value: "Por Renovar", label: "Por Renovar" },
  { value: "Por Reprogramar", label: "Por Reprogramar" },
]; */

// Función para determinar si es una imagen
const isImageFile = (fileData) => {
  if (!fileData) return false;
  
  // Si es una URL
  if (fileData.startsWith('http')) {
    const extension = fileData.split('?')[0].toLowerCase();
    return extension.endsWith('.jpg') || 
           extension.endsWith('.jpeg') || 
           extension.endsWith('.png') || 
           extension.endsWith('.gif') || 
           extension.endsWith('.webp') ||
           fileData.includes('/image/');
  }
  
  // Si es base64
  return fileData.includes("data:image/") || 
        (fileData.includes("base64") && !fileData.includes("JVBERi0"));
};

// Función para determinar si es un PDF
const isPdfFile = (fileData) => {
  if (!fileData) return false;
  
  // Si es una URL
  if (fileData.startsWith('http')) {
    const extension = fileData.split('?')[0].toLowerCase();
    return extension.endsWith('.pdf') || 
           fileData.includes('/pdf/') ||
           fileData.includes('content-type=application%2Fpdf');
  }
  
  // Si es base64
  return fileData.includes("data:application/pdf") || 
         fileData.includes("JVBERi0"); // Firma de PDF en base64
};

// Formatear fechas
const formatearFecha = (fecha) => {
  if (!fecha) return "";
  return fecha instanceof Date
    ? fecha.toISOString().split("T")[0]
    : new Date(fecha).toISOString().split("T")[0];
};

const ReporteFacturacion = () => {
  const dispatch = useDispatch();
  document.title = "Reporte de Facturación | Farme";

  // Limpiar datos de facturación al montar el componente
  useEffect(() => {
    dispatch(reset_solicitud_flag());
  }, [dispatch]);

  // Obtener datos de sesión
  const perfil = DatosSesion("perfil");
  const farmaciaId = DatosSesion("farmacia");

  const isAdmin = perfil === "Administrador" || perfil === "MyHealth";

  // Estados
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [farmacia, setFarmacia] = useState(isAdmin ? null : farmaciaId);
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);
  
  // Estados para la paginación
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [filteredDataCount, setFilteredDataCount] = useState(0);
  
  // Estados para el visor de archivos
  const [modalOpen, setModalOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState({
    url: null,
    name: "",
    type: ""
  });

  // Función para abrir archivo en modal
  const abrirArchivo = (fileData, fileName) => {
    if (!fileData) {
      toast.error("No hay archivo disponible");
      return;
    }
    
    if (fileData.startsWith('http')) {
      // Usar URL prefirmada para archivos en DO Spaces
      getUrlPrefirmada(fileData)
        .then(response => {
          if (response.data && response.data.url) {
            const prefirmedUrl = response.data.url;
            const fileType = isPdfFile(fileData) ? "pdf" : isImageFile(fileData) ? "image" : "other";
            
            setCurrentFile({
              url: prefirmedUrl,
              name: fileName || "Factura",
              type: fileType
            });
            setModalOpen(true);
          } else {
            toast.error("No se pudo obtener la URL prefirmada");
          }
        })
        .catch(error => {
          console.error("Error:", error);
          toast.error("Error al obtener la URL prefirmada");
        });
    } else if (isImageFile(fileData) || isPdfFile(fileData)) {
      const fileType = isPdfFile(fileData) ? "pdf" : "image";
      setCurrentFile({
        url: fileData,
        name: fileName || "Factura",
        type: fileType
      });
      setModalOpen(true);
    } else {
      toast.error("Formato de archivo no soportado");
    }
  };
  const [tipo, setTipo] = useState(null);
  const [estatusPago, setEstatusPago] = useState(null);
  const [aseguradora, setAseguradora] = useState(null);
  const [estadoGeografico, setEstadoGeografico] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    fechaInicio: false,
    fechaFin: false,
  });

  // Selectores de Redux
  const farmacias = useSelector((state) => state.Farmacias.farmacias);
  //const facturacion = useSelector((state) => state.Solicitudes.facturacion);

  const facturacion = useSelector((state) => {
    //console.log("Estado completo de Solicitudes:", state.Solicitudes);
    return state.Solicitudes.facturacion;
  });

  // Convertir las farmacias a opciones para el Select
  const opcionesFarmacias = useMemo(
    () => [
      ...(isAdmin ? [{ value: "", label: "Todas las farmacias" }] : []),
      ...(Array.isArray(farmacias) && farmacias.length > 0
        ? farmacias[0].map((farm) => ({
            value: farm.codigo_myh,
            label: farm.nombre,
          }))
        : []),
    ],
    [farmacias, isAdmin]
  );

  // Filtrar datos por estatus de pago y estado geográfico
  const datosFiltrados = useMemo(() => {
    if (!facturacion?.detalles) return [];
    
    let datos = facturacion.detalles;
    
    // Filtrar por estatus de pago
    if (estatusPago && estatusPago.value !== "Todos") {
      datos = datos.filter(detalle => {
        const estatus = detalle.estatus_pago;
        
        // Pendiente: sin estatus (null/undefined) O con estatus "pendiente"
        if (estatusPago.value === "Pendiente") {
          return !estatus || estatus.toLowerCase() === "pendiente";
        }
        
        // Por Pagar: estatus exacto "por pagar"
        if (estatusPago.value === "Por Pagar") {
          return estatus && estatus.toLowerCase() === "por pagar";
        }
        
        return estatus && estatus.toLowerCase() === estatusPago.value.toLowerCase();
      });
    }
    
    // Filtrar por estado geográfico
    if (estadoGeografico) {
      datos = datos.filter(detalle => {
        const direccion = detalle.direccion;
        if (!direccion) return false;
        
        // Extraer el estado de la dirección
        const estadoMatch = direccion.match(/Estado\s+([^,]+)(?:,|$)/i);
        if (estadoMatch && estadoMatch[1]) {
          const estadoEnDireccion = estadoMatch[1].trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const estadoFiltro = estadoGeografico.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return estadoEnDireccion.includes(estadoFiltro) || estadoFiltro.includes(estadoEnDireccion);
        }
        return false;
      });
    }
    
    return datos;
  }, [facturacion, estatusPago, estadoGeografico]);

  // Calcular estadísticas de facturación basadas en datos filtrados
  const estadisticasFacturacion = useMemo(() => {
    if (!datosFiltrados || datosFiltrados.length === 0) return null;
    
    const montosDolar = datosFiltrados.map(d => d.monto_dolar || 0);
    const montosBs = datosFiltrados.map(d => d.monto_total || 0);
    const total = datosFiltrados.length;
    const sumaDolar = montosDolar.reduce((a, b) => a + b, 0);
    const sumaBs = montosBs.reduce((a, b) => a + b, 0);
    
    return {
      total_facturas: total,
      total_monto_dolar: sumaDolar,
      total_monto_bs: sumaBs,
      promedio_monto_dolar: total > 0 ? sumaDolar / total : 0,
      promedio_monto_bs: total > 0 ? sumaBs / total : 0,
      monto_maximo_dolar: Math.max(...montosDolar),
      monto_maximo_bs: Math.max(...montosBs),
      monto_minimo_dolar: Math.min(...montosDolar),
      monto_minimo_bs: Math.min(...montosBs)
    };
  }, [datosFiltrados]);

  // Calcular estadísticas de pagos basadas en datos filtrados
  const estadisticasPagos = useMemo(() => {
    if (!datosFiltrados || datosFiltrados.length === 0) return null;
    
    const stats = {
      total: datosFiltrados.length,
      pagadas: 0,
      porPagar: 0,
      pendientes: 0,
      rechazadas: 0,
      parciales: 0,
      monto_pagado: 0,
      monto_pagado_bs: 0,
      monto_por_pagar: 0,
      monto_por_pagar_bs: 0,
      monto_pendiente: 0,
      monto_pendiente_bs: 0
    };
    
    datosFiltrados.forEach(detalle => {
      // estatus_pago puede ser null, undefined, o un string
      const estatus = detalle.estatus_pago ? detalle.estatus_pago.toLowerCase() : null;
      const montoDolar = detalle.monto_dolar || 0;
      const montoBs = detalle.monto_total || 0;
      
      // Pendientes: sin estatus (null/undefined) O con estatus "pendiente"
      if (!estatus || estatus === "pendiente") {
        stats.pendientes++;
        stats.monto_pendiente += montoDolar;
        stats.monto_pendiente_bs += montoBs;
      } else if (estatus === "pagada" || estatus === "pagado") {
        stats.pagadas++;
        stats.monto_pagado += montoDolar;
        stats.monto_pagado_bs += montoBs;
      } else if (estatus === "por pagar") {
        stats.porPagar++;
        stats.monto_por_pagar += montoDolar;
        stats.monto_por_pagar_bs += montoBs;
      } else if (estatus === "rechazada" || estatus === "rechazado") {
        stats.rechazadas++;
      } else if (estatus === "parcial") {
        stats.parciales++;
      }
    });
    
    // Calcular porcentajes correctos
    stats.porcentaje_pagadas = stats.total > 0 ? (stats.pagadas / stats.total * 100).toFixed(1) : 0;
    stats.porcentaje_por_pagar = stats.total > 0 ? (stats.porPagar / stats.total * 100).toFixed(1) : 0;
    stats.porcentaje_pendientes = stats.total > 0 ? (stats.pendientes / stats.total * 100).toFixed(1) : 0;
    stats.porcentaje_rechazadas = stats.total > 0 ? (stats.rechazadas / stats.total * 100).toFixed(1) : 0;
    stats.porcentaje_parciales = stats.total > 0 ? (stats.parciales / stats.total * 100).toFixed(1) : 0;
    
    return stats;
  }, [datosFiltrados]);

  // Definir las columnas para la tabla de facturación
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "identificador_solicitud",
        enableColumnFilter: false,
      },
      {
        header: "Tipo",
        accessorKey: "tipo_solicitud",
        enableColumnFilter: false,
        cell: (cell) => {
          const tipo_solicitud = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!tipo_solicitud) {
            return <span>-</span>;
          }

          let badgeClass = "";
          let colorClass = "";

          switch (tipo_solicitud) {
            case "Aguda":
              badgeClass = "Aguda";
              colorClass = "bg-danger-subtle text-danger";
              break;
            case "Cronica":
              badgeClass = "Crónica";
              colorClass = "bg-success-subtle text-success";
              break;
            default:
              colorClass = "bg-info-subtle text-info";
          }

          return (
            <span className={`badge ${colorClass}`}>{badgeClass || tipo_solicitud}</span>
          );
        },
      },
      {
        header: "Solicitante",
        accessorKey: "solicitante",
        enableColumnFilter: false,
        cell: (cell) => {
          const nombre = cell.getValue();
          const cedula = cell.row.original.cedula;
          return (
            <div>
              <div className="fw-medium">{nombre}</div>
              {cedula && (
                <div className="text-muted mb-0" style={{fontSize: '12px'}}>
                  {cedula}
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Cod. Farmacia",
        accessorKey: "codigoProveedor",
        enableColumnFilter: false,
        cell: (cell) => {
          const codigoProveedor = cell.getValue();
          // Verificar si estatus es null o undefined
          if (!codigoProveedor) {
            return <span>-</span>;
          }

          let badgeClass = "";
          let colorClass = "";

          switch (codigoProveedor) {
            case "00077":
              badgeClass = "Caracas";
              colorClass = "bg-primary-subtle text-info";
              break;
            case "00082":
              badgeClass = "Mérida";
              colorClass = "bg-danger-subtle text-info";
              break;
            case "00083":
              badgeClass = "Farmaignacio";
              colorClass = "bg-warning-subtle text-info";
              break;
            case "00073":
              badgeClass = "Lara";
              colorClass = "bg-warning-subtle text-info";
              break;
            case "00088":
              badgeClass = "Farmadon";
              colorClass = "bg-warning-subtle text-info";
              break;
              case "00100":
              badgeClass = "Dimaworld";
              colorClass = "bg-warning-subtle text-info";
              break;
              case "00099":
              badgeClass = "Farme";
              colorClass = "bg-warning-subtle text-info";
              break;
            default:
              badgeClass = "S/F";
              colorClass = "bg-warning-subtle text-warning";
          }

          return (
            <span className={`badge ${colorClass}`}>
              {codigoProveedor} - {badgeClass}
            </span>
          );
        },
      },
      {
        header: "Estado",
        accessorKey: "direccion",
        enableColumnFilter: false,
        cell: (cell) => {
          const direccion = cell.getValue();
          // Verificar si dirección es null o undefined
          if (!direccion) {
            return <span>-</span>;
          }
          
          // Extraer solo el estado de la dirección completa
          const estadoMatch = direccion.match(/Estado\s+([^,]+)(?:,|$)/);
          if (estadoMatch && estadoMatch[1]) {
            // Convertir a formato título (primera letra de cada palabra en mayúscula)
            const estadoTitulo = estadoMatch[1].trim().toLowerCase().split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            return <span>{estadoTitulo}</span>;
          } else {
            return <span> - </span>; 
          }
        },
      },
      {
        header: "Fecha Factura",
        accessorKey: "fecha_factura",
        enableColumnFilter: false,
      },
      {
        header: "Número Factura",
        accessorKey: "numero_factura",
        enableColumnFilter: false,
      },
      {
        header: "Monto Total (Bs)",
        accessorKey: "monto_total",
        enableColumnFilter: false,
        cell: (cell) => {
          const monto = cell.getValue();
          return `Bs. ${monto.toLocaleString("es-VE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
      {
        header: "Monto Total ($)",
        accessorKey: "monto_dolar",
        enableColumnFilter: false,
        cell: (cell) => {
          const montoDolar = cell.getValue();
          if (!montoDolar || montoDolar === 0) {
            return <span className="text-muted">-</span>;
          }
          return `$ ${montoDolar.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        },
      },
      {
        header: "Estatus Solicitud",
        accessorKey: "estatus",
        enableColumnFilter: false,
        cell: (cell) => {
          const estatus = cell.getValue();
          if (!estatus) return <span className="text-muted">-</span>;

          switch (estatus.toLowerCase()) {
            case "entregado":
              return <span className="badge bg-success-subtle text-success">Entregado</span>;
            case "nueva solicitud":
              return <span className="badge bg-info-subtle text-info">Nueva Solicitud</span>;
            case "por entregar":
              return <span className="badge bg-primary-subtle text-primary">Por Entregar</span>;
            case "en transito":
            case "en tránsito":
              return <span className="badge bg-dark-subtle text-dark">En Tránsito</span>;
            case "cancelado":
              return <span className="badge bg-danger-subtle text-danger">Cancelado</span>;
            case "por renovar":
              return <span className="badge bg-secondary-subtle text-secondary">Por Renovar</span>;
            case "por reprogramar":
              return <span className="badge bg-warning-subtle text-warning">Por Reprogramar</span>;
            default:
              return <span className="badge bg-light text-dark">{estatus}</span>;
          }
        },
      },
      {
        header: "Estatus Pago",
        accessorKey: "estatus_pago",
        enableColumnFilter: false,
        cell: (cell) => {
          const estatusPago = cell.getValue();
          const tracking = cell.row.original.tracking_pago;
          const tooltipId = `tooltip-pago-${cell.row.original.id_solicitud}`;
          
          const renderBadge = (estatus) => {
            if (!estatus) {
              return (
                <span className="badge bg-secondary-subtle text-secondary">
                  <i className="ri-time-line me-1"></i>
                  Pendiente
                </span>
              );
            }

            switch (estatus.toLowerCase()) {
              case "pagada":
              case "pagado":
                return (
                  <span className="badge bg-success-subtle text-success">
                    <i className="ri-checkbox-circle-line me-1"></i>
                    Pagada
                  </span>
                );
              case "pendiente":
                return (
                  <span className="badge bg-warning-subtle text-warning">
                    <i className="ri-time-line me-1"></i>
                    Pendiente
                  </span>
                );
              case "rechazada":
              case "rechazado":
                return (
                  <span className="badge bg-danger-subtle text-danger">
                    <i className="ri-close-circle-line me-1"></i>
                    Rechazada
                  </span>
                );
              case "parcial":
                return (
                  <span className="badge bg-info-subtle text-info">
                    <i className="ri-information-line me-1"></i>
                    Parcial
                  </span>
                );
              default:
                return (
                  <span className="badge bg-light text-dark">
                    {estatus}
                  </span>
                );
            }
          };

          if (!tracking) {
            return renderBadge(estatusPago);
          }

          return (
            <>
              <span id={tooltipId} style={{ cursor: 'pointer' }}>
                {renderBadge(estatusPago)}
              </span>
              <UncontrolledTooltip placement="top" target={tooltipId}>
                <div className="text-start">
                  <strong>Detalles del Pago:</strong>
                  {tracking.fecha_pago && (
                    <div className="mt-1">
                      <i className="ri-calendar-line me-1"></i>
                      Fecha: {tracking.fecha_pago}
                    </div>
                  )}
                  {tracking.monto_pagado !== null && tracking.monto_pagado !== undefined && (
                    <div>
                      <i className="ri-money-dollar-circle-line me-1"></i>
                      Pagado: ${tracking.monto_pagado?.toFixed(2)}
                      {tracking.monto_pagado_bs && ` (Bs. ${tracking.monto_pagado_bs?.toFixed(2)})`}
                    </div>
                  )}
                  {tracking.diferencia_pago !== null && tracking.diferencia_pago !== undefined && tracking.diferencia_pago !== 0 && (
                    <div className={tracking.diferencia_pago > 0 ? 'text-success' : 'text-danger'}>
                      <i className="ri-arrow-up-down-line me-1"></i>
                      Diferencia: ${tracking.diferencia_pago?.toFixed(2)}
                      {tracking.diferencia_pago_bs && ` (Bs. ${tracking.diferencia_pago_bs?.toFixed(2)})`}
                    </div>
                  )}
                  {tracking.deducible !== null && tracking.deducible !== undefined && tracking.deducible > 0 && (
                    <div className="text-warning">
                      <i className="ri-subtract-line me-1"></i>
                      Deducible: ${tracking.deducible?.toFixed(2)}
                      {tracking.deducible_bs && ` (Bs. ${tracking.deducible_bs?.toFixed(2)})`}
                    </div>
                  )}
                  {tracking.no_amparado !== null && tracking.no_amparado !== undefined && tracking.no_amparado > 0 && (
                    <div className="text-danger">
                      <i className="ri-close-circle-line me-1"></i>
                      No Amparado: ${tracking.no_amparado?.toFixed(2)}
                      {tracking.no_amparado_bs && ` (Bs. ${tracking.no_amparado_bs?.toFixed(2)})`}
                    </div>
                  )}
                  {tracking.observaciones && (
                    <div className="mt-1 text-muted" style={{ fontSize: '0.85em' }}>
                      <i className="ri-information-line me-1"></i>
                      {tracking.observaciones}
                    </div>
                  )}
                </div>
              </UncontrolledTooltip>
            </>
          );
        },
      },
    ],
    []
  );

  const hayFacturas = useMemo(() => {
    //console.log("Verificando facturas en:", facturacion);
    return facturacion?.detalles && facturacion.detalles.length > 0;
  }, [facturacion]);

  // Función para generar el reporte
  const generarReporte = useCallback(async () => {
    setErrors({
      fechaInicio: false,
      fechaFin: false,
    });

    let hasErrors = false;
    if (!fechaInicio) {
      setErrors((prev) => ({ ...prev, fechaInicio: true }));
      hasErrors = true;
    }
    if (!fechaFin) {
      setErrors((prev) => ({ ...prev, fechaFin: true }));
      hasErrors = true;
    }

    if (hasErrors) return;

    try {
      setIsLoading(true);


      // Obtener el código_myh correcto para la farmacia del usuario no admin
      let codigoFarmacia = "";
      if (isAdmin) {
        codigoFarmacia = farmacia?.value || "";
      } else {
        // Si hay farmacias disponibles, buscar la que coincide con farmaciaId
        if (Array.isArray(farmacias) && farmacias.length > 0) {
          const farmaciaEncontrada = farmacias[0].find(
            (f) => f.id === farmaciaId
          );
          if (farmaciaEncontrada) {
            codigoFarmacia = farmaciaEncontrada.codigo_myh;
          }
        }
      }
     
      const params = {
        tipo: tipo?.value || "Todas",
        farmacia: codigoFarmacia,
        estatus_pago: estatusPago?.value || "Todos",
        aseguradora: aseguradora?.value || "Todas",
        fecha_inicio: formatearFecha(fechaInicio),
        fecha_fin: formatearFecha(fechaFin),
      };

      await dispatch(obtenerFacturacionPorPeriodo(params));
      setBusquedaRealizada(true);
      setPageIndex(0); // Resetear a la primera página cuando se genera un nuevo reporte 
    } catch (error) {
      console.error("Error al obtener facturación:", error);
      toast.error("Error al cargar los datos. Por favor intente nuevamente.");
    } finally {
      setIsLoading(false);
    }
  }, [
    fechaInicio,
    fechaFin,
    tipo,
    farmacia,
    aseguradora,
    dispatch,
    isAdmin,
    farmaciaId,
    farmacias,
  ]);

  // Función para exportar a Excel
  const exportarExcel = useCallback(async () => {
    setErrors({
      fechaInicio: false,
      fechaFin: false,
    });

    let hasErrors = false;
    if (!fechaInicio) {
      setErrors((prev) => ({ ...prev, fechaInicio: true }));
      hasErrors = true;
    }
    if (!fechaFin) {
      setErrors((prev) => ({ ...prev, fechaFin: true }));
      hasErrors = true;
    }

    if (hasErrors) return;

    try {
      setIsExporting(true);

      let codigoFarmacia = "";
      if (isAdmin) {
        codigoFarmacia = farmacia?.value || "";
      } else {
        // Si hay farmacias disponibles, buscar la que coincide con farmaciaId
        if (
          Array.isArray(farmacias) &&
          farmacias.length > 0 &&
          Array.isArray(farmacias[0])
        ) {
          const listaFarmacias = farmacias[0];
          const farmaciaEncontrada = listaFarmacias.find(
            (f) => f.id === farmaciaId
          );
          if (farmaciaEncontrada) {
            codigoFarmacia = farmaciaEncontrada.codigo_myh;
          } else {
            // Si no se encuentra, usar directamente el farmaciaId
            codigoFarmacia = farmaciaId;
          }
        } else {
          // Si no hay farmacias disponibles, usar el farmaciaId directamente
          codigoFarmacia = farmaciaId;
        }
      }

      const params = {
        tipo: tipo?.value || "Todas",
        farmacia: codigoFarmacia,
        estatus_pago: estatusPago?.value || "Todos",
        aseguradora: aseguradora?.value || "Todas",
        estado_geografico: estadoGeografico || "",
        fecha_inicio: formatearFecha(fechaInicio),
        fecha_fin: formatearFecha(fechaFin),
      };

      await dispatch(exportarReporteFacturacion(params)).unwrap();
      toast.success("Reporte exportado exitosamente");
    } catch (error) {
      console.error("Error al exportar:", error);
      toast.error(error.message || "Error al exportar el reporte");
    } finally {
      setIsExporting(false);
    }
  }, [
    fechaInicio,
    fechaFin,
    tipo,
    farmacia,
    estatusPago,
    aseguradora,
    estadoGeografico,
    dispatch,
    isAdmin,
    farmaciaId,
    farmacias,
  ]);

  useEffect(() => {
    dispatch(getFarmacias());
  }, [dispatch]);

  // Efecto separado para preseleccionar la farmacia cuando las farmacias están disponibles
  useEffect(() => {
    // Si el usuario no es admin y tenemos farmacias disponibles, preseleccionar su farmacia
    if (
      !isAdmin &&
      farmaciaId &&
      Array.isArray(farmacias) &&
      farmacias.length > 0
    ) {
      const farmaciaEncontrada = farmacias[0].find(
        (f) => f.codigo_myh === farmaciaId
      );
      if (farmaciaEncontrada) {
        setFarmacia({
          value: farmaciaEncontrada.codigo_myh,
          label: farmaciaEncontrada.nombre,
        });
      }
    }
  }, [isAdmin, farmaciaId, farmacias]);

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          {/* Cards de Estadísticas de Facturación */}
          {(isLoading || estadisticasFacturacion) && (
            <Row className="mb-3">
              <Col xs={12}>
                <h6 className="text-muted mb-3">
                  <i className="ri-file-list-line me-2"></i>
                  Estadísticas de Facturación
                </h6>
              </Col>
              {/* Total Facturas */}
              <Col xl={3} md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-success)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-2">
                              Total Facturas
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-2">
                              {estadisticasFacturacion?.total_facturas || 0}
                            </h4>
                            <div className="border-top pt-2 mt-1">
                              <p className="text-muted mb-1 d-flex justify-content-between" style={{ fontSize: '0.85em' }}>
                                <span>Total USD:</span>
                                <strong className="text-success">$ {estadisticasFacturacion?.total_monto_dolar?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}</strong>
                              </p>
                              <p className="text-muted mb-0 d-flex justify-content-between" style={{ fontSize: '0.85em' }}>
                                <span>Total Bs:</span>
                                <strong>Bs. {estadisticasFacturacion?.total_monto_bs?.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}</strong>
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                              <i className="ri-file-list-3-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              {/* Promedio Montos */}
              <Col xl={3} md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-info)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-6"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Promedio Montos
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              $ {estadisticasFacturacion?.promedio_monto_dolar?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                            </h4>
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85em' }}>
                              Bs. {estadisticasFacturacion?.promedio_monto_bs?.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                              <i className="ri-money-dollar-circle-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              {/* Monto Máximo */}
              <Col xl={3} md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-warning)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-6"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Monto Máximo
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              $ {estadisticasFacturacion?.monto_maximo_dolar?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                            </h4>
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85em' }}>
                              Bs. {estadisticasFacturacion?.monto_maximo_bs?.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                              <i className="ri-arrow-up-circle-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              {/* Monto Mínimo */}
              <Col xl={3} md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-danger)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-6"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Monto Mínimo
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              $ {estadisticasFacturacion?.monto_minimo_dolar?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                            </h4>
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.85em' }}>
                              Bs. {estadisticasFacturacion?.monto_minimo_bs?.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                              <i className="ri-arrow-down-circle-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Cards de Estadísticas de Pagos */}
          {(isLoading || estadisticasPagos) && (
            <Row className="mb-3">
              <Col xs={12}>
                <h6 className="text-muted mb-3">
                  <i className="ri-money-dollar-circle-line me-2"></i>
                  Estadísticas de Pagos
                </h6>
              </Col>
              {/* Facturas Pagadas */}
              <Col xl md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-success)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Facturas Pagadas
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              {estadisticasPagos?.pagadas || 0}
                            </h4>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                              <i className="ri-checkbox-circle-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLoading && (
                      <div className="mt-3">
                        <span className="badge bg-success-subtle text-success">
                          {estadisticasPagos?.porcentaje_pagadas || 0}%
                        </span>
                        <span className="text-muted ms-1">
                          $ {estadisticasPagos?.monto_pagado?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                        </span>
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>
                          Bs. {estadisticasPagos?.monto_pagado_bs?.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              {/* Facturas Por Pagar */}
              <Col xl md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-primary)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Por Pagar
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              {estadisticasPagos?.porPagar || 0}
                            </h4>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                              <i className="ri-wallet-3-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLoading && (
                      <div className="mt-3">
                        <span className="badge bg-primary-subtle text-primary">
                          {estadisticasPagos?.porcentaje_por_pagar || 0}%
                        </span>
                        <span className="text-muted ms-1">
                          $ {estadisticasPagos?.monto_por_pagar?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                        </span>
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>
                          Bs. {estadisticasPagos?.monto_por_pagar_bs?.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              {/* Facturas Pendientes */}
              <Col xl md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-warning)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Facturas Pendientes
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              {estadisticasPagos?.pendientes || 0}
                            </h4>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                              <i className="ri-time-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLoading && (
                      <div className="mt-3">
                        <span className="badge bg-warning-subtle text-warning">
                          {estadisticasPagos?.porcentaje_pendientes || 0}%
                        </span>
                        <span className="text-muted ms-1">
                          $ {estadisticasPagos?.monto_pendiente?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0.00"}
                        </span>
                        <div className="text-muted" style={{ fontSize: '0.8em' }}>
                          Bs. {estadisticasPagos?.monto_pendiente_bs?.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "0,00"}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              {/* Facturas Rechazadas */}
              <Col xl md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-danger)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Facturas Rechazadas
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              {estadisticasPagos?.rechazadas || 0}
                            </h4>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                              <i className="ri-close-circle-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLoading && (
                      <div className="mt-3">
                        <span className="badge bg-danger-subtle text-danger">
                          {estadisticasPagos?.porcentaje_rechazadas || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
              {/* Pagos Parciales */}
              <Col xl md={6}>
                <div 
                  className="card card-animate overflow-hidden h-100" 
                  style={{ borderLeft: '4px solid var(--vz-info)' }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1 overflow-hidden">
                        {isLoading ? (
                          <div className="placeholder-glow">
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              <span className="placeholder col-8"></span>
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              <span className="placeholder col-4"></span>
                            </h4>
                          </div>
                        ) : (
                          <>
                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                              Pagos Parciales
                            </p>
                            <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                              {estadisticasPagos?.parciales || 0}
                            </h4>
                          </>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {isLoading ? (
                          <div className="avatar-sm placeholder-glow">
                            <span className="placeholder rounded" style={{ width: '100%', height: '100%' }}></span>
                          </div>
                        ) : (
                          <div className="avatar-sm">
                            <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                              <i className="ri-information-line"></i>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {!isLoading && (
                      <div className="mt-3">
                        <span className="badge bg-info-subtle text-info">
                          {estadisticasPagos?.porcentaje_parciales || 0}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader style={{ backgroundColor: '#405189' }}>
                  <div className="d-md-flex align-items-center">
                    <h5 className="card-title mb-3 mb-md-0 flex-grow-1 text-white">
                      <i className="ri-file-list-3-line me-2"></i>
                      Reporte de Facturación
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          color="success"
                          onClick={exportarExcel}
                          disabled={isExporting || !fechaInicio || !fechaFin}
                          title={
                            !fechaInicio || !fechaFin
                              ? "Seleccione un rango de fechas"
                              : "Exportar a Excel"
                          }
                        >
                          {isExporting ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Exportando...
                            </>
                          ) : (
                            <>
                              <i className="ri-file-excel-2-line align-bottom me-1"></i>
                              Exportar Excel
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="border border-dashed border-end-0 border-start-0 py-3">
                  <Form>
                    <Row className="g-2 align-items-end">
                      {/* Rango de Fechas */}
                      <Col xl={3} lg={4} md={6}>
                        <Label htmlFor="fechaRango" className="form-label mb-1">
                          Rango de Fechas <span className="text-danger">*</span>
                        </Label>
                        <Flatpickr
                          className={`form-control form-control-sm ${
                            errors.fechaInicio || errors.fechaFin ? "is-invalid" : ""
                          }`}
                          options={{
                            mode: "range",
                            dateFormat: "Y-m-d",
                            altInput: true,
                            altFormat: "d/m/Y",
                            locale: Spanish,
                          }}
                          value={fechaInicio && fechaFin ? [fechaInicio, fechaFin] : []}
                          onChange={(dates) => {
                            if (dates.length === 2) {
                              setFechaInicio(dates[0]);
                              setFechaFin(dates[1]);
                              setErrors({ fechaInicio: false, fechaFin: false });
                            } else if (dates.length === 1) {
                              setFechaInicio(dates[0]);
                              setFechaFin(null);
                            }
                          }}
                          placeholder="Seleccione rango..."
                        />
                      </Col>
                      {/* Tipo */}
                      <Col xl={2} lg={2} md={6}>
                        <Label htmlFor="tipo" className="form-label mb-1">
                          Tipo
                        </Label>
                        <Select
                          value={tipo}
                          onChange={setTipo}
                          options={opcionesTipo}
                          classNamePrefix="select2-selection"
                          placeholder="Todas"
                          styles={{
                            control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                          }}
                        />
                      </Col>
                      {/* Aseguradora */}
                      <Col xl={2} lg={2} md={6}>
                        <Label htmlFor="aseguradora" className="form-label mb-1">
                          Aseguradora
                        </Label>
                        <Select
                          value={aseguradora}
                          onChange={setAseguradora}
                          options={opcionesAseguradora}
                          classNamePrefix="select2-selection"
                          placeholder="Todas"
                          isClearable
                          styles={{
                            control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                          }}
                        />
                      </Col>
                      {/* Farmacia */}
                      <Col xl={2} lg={3} md={6}>
                        <Label htmlFor="farmacia" className="form-label mb-1">
                          Farmacia
                        </Label>
                        <Select
                          value={farmacia}
                          onChange={setFarmacia}
                          options={opcionesFarmacias}
                          classNamePrefix="select2-selection"
                          placeholder="Todas"
                          isClearable={isAdmin}
                          isDisabled={!isAdmin}
                          styles={{
                            control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                          }}
                        />
                      </Col>
                      {/* Estatus Pago */}
                      <Col xl={2} lg={3} md={6}>
                        <Label htmlFor="estatusPago" className="form-label mb-1">
                          Estatus Pago
                        </Label>
                        <Select
                          value={estatusPago}
                          onChange={setEstatusPago}
                          options={opcionesEstatusPago}
                          classNamePrefix="select2-selection"
                          placeholder="Todos"
                          isClearable
                          styles={{
                            control: (base) => ({ ...base, minHeight: '31px', height: '31px' }),
                            valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                            input: (base) => ({ ...base, margin: 0, padding: 0 }),
                            indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                          }}
                        />
                      </Col>
                      {/* Estado Geográfico */}
                      <Col xl={2} lg={3} md={6}>
                        <Label htmlFor="estadoGeografico" className="form-label mb-1">
                          Estado
                        </Label>
                        <Input
                          type="select"
                          id="estadoGeografico"
                          value={estadoGeografico}
                          onChange={(e) => setEstadoGeografico(e.target.value)}
                          className="form-select form-select-sm"
                        >
                          <option value="">Todos</option>
                          <option value="Amazonas">Amazonas</option>
                          <option value="Anzoátegui">Anzoátegui</option>
                          <option value="Apure">Apure</option>
                          <option value="Aragua">Aragua</option>
                          <option value="Barinas">Barinas</option>
                          <option value="Bolívar">Bolívar</option>
                          <option value="Carabobo">Carabobo</option>
                          <option value="Cojedes">Cojedes</option>
                          <option value="Delta Amacuro">Delta Amacuro</option>
                          <option value="Distrito Capital">Distrito Capital</option>
                          <option value="Falcón">Falcón</option>
                          <option value="Guárico">Guárico</option>
                          <option value="La Guaira">La Guaira</option>
                          <option value="Lara">Lara</option>
                          <option value="Mérida">Mérida</option>
                          <option value="Miranda">Miranda</option>
                          <option value="Monagas">Monagas</option>
                          <option value="Nueva Esparta">Nueva Esparta</option>
                          <option value="Portuguesa">Portuguesa</option>
                          <option value="Sucre">Sucre</option>
                          <option value="Táchira">Táchira</option>
                          <option value="Trujillo">Trujillo</option>
                          <option value="Yaracuy">Yaracuy</option>
                          <option value="Zulia">Zulia</option>
                        </Input>
                      </Col>
                      {/* Botón Filtrar */}
                      <Col xl={1} lg={2} md={6}>
                        <Button
                          type="button"
                          color="primary"
                          size="sm"
                          className="w-100"
                          onClick={generarReporte}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <>
                              <i className="ri-search-line me-1"></i>
                              Buscar
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </CardBody>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center my-5">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <div className="mt-2">Cargando datos...</div>
                    </div>
                  ) : !busquedaRealizada ? (
                    <div className="text-center my-5">
                      <i className="ri-filter-line fs-1 text-muted"></i>
                      <p className="mt-2">
                        Seleccione los filtros y haga clic en "Filtrar" para ver
                        las facturas
                      </p>
                    </div>
                  ) : !hayFacturas ? (
                    <div className="text-center my-5">
                      <i className="ri-file-search-line fs-1 text-muted"></i>
                      <p className="mt-2">
                        No se encontraron facturas con los filtros
                        seleccionados
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Sección de facturas por tipo - FILTRADO */}
                      {datosFiltrados && datosFiltrados.length > 0 && (() => {
                        // Calcular resumen por tipo basado en datos filtrados
                        const resumenPorTipo = {};
                        datosFiltrados.forEach(detalle => {
                          const tipo = detalle.tipo_solicitud;
                          if (!resumenPorTipo[tipo]) {
                            resumenPorTipo[tipo] = {
                              cantidad: 0,
                              monto_total: 0,
                              monto_total_dolar: 0
                            };
                          }
                          resumenPorTipo[tipo].cantidad++;
                          resumenPorTipo[tipo].monto_total += detalle.monto_total || 0;
                          resumenPorTipo[tipo].monto_total_dolar += detalle.monto_dolar || 0;
                        });
                        
                        return Object.keys(resumenPorTipo).length > 0 ? (
                          <div className="mb-4">
                            <h6 className="mb-3">
                              Resumen por Tipo de Solicitud
                              {(estatusPago && estatusPago.value !== "Todos") || estadoGeografico ? (
                                <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                                  (Filtrado por: {[
                                    estatusPago && estatusPago.value !== "Todos" ? estatusPago.label : null,
                                    estadoGeografico ? `Estado ${estadoGeografico}` : null
                                  ].filter(Boolean).join(", ")})
                                </span>
                              ) : null}
                            </h6>
                            <Row>
                              {Object.entries(resumenPorTipo).map(
                                ([tipo, datos]) => (
                                <Col key={tipo} md={6} lg={4}>
                                  <Card className="border">
                                    <CardBody>
                                      <h6 className="card-title mb-2">
                                        {tipo}
                                      </h6>
                                      <div className="d-flex justify-content-between">
                                        <span>Cantidad:</span>
                                        <span className="fw-medium">
                                          {datos.cantidad}
                                        </span>
                                      </div>
                                      <div className="d-flex justify-content-between">
                                        <span>Monto Total (Bs):</span>
                                        <span className="fw-medium">
                                          Bs.{" "}
                                          {datos.monto_total.toLocaleString(
                                            "es-VE",
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }
                                          )}
                                        </span>
                                      </div>
                                      {datos.monto_total_dolar && (
                                        <div className="d-flex justify-content-between">
                                          <span>Monto Total ($):</span>
                                          <span className="fw-medium text-success">
                                            ${" "}
                                            {datos.monto_total_dolar.toLocaleString(
                                              "en-US",
                                              {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              }
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </CardBody>
                                  </Card>
                                </Col>
                              )
                              )}
                            </Row>
                          </div>
                        ) : null;
                      })()}

                      {facturacion?.detalles ? (
                        <>
                          <TableContainerNoPagination
                            columns={columns}
                            data={datosFiltrados}
                            isGlobalFilter={true}
                            customPageSize={pageSize}
                            customPageIndex={pageIndex}
                            onPageChange={setPageIndex}
                            onFilteredDataChange={setFilteredDataCount}
                            globalFilter={globalFilter}
                            onGlobalFilterChange={setGlobalFilter}
                            divClass="table-responsive"
                            onRowClick={(row) => {
                              window.open(`/solicitud/ver/${row.id_solicitud}`, '_blank');
                            }}
                          />
                          
                          {estatusPago && estatusPago.value !== "Todos" && (
                            <div className="alert alert-info mt-3">
                              <i className="ri-filter-line me-2"></i>
                              Mostrando solo facturas con estatus de pago: <strong>{estatusPago.label}</strong>
                              {' '}
                              <button 
                                className="btn btn-sm btn-link p-0 ms-2"
                                onClick={() => setEstatusPago(null)}
                              >
                                Limpiar filtro
                              </button>
                            </div>
                          )}

                          {/* Paginador personalizado */}
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="d-flex align-items-center">
                              <span className="me-2 text-muted" style={{fontSize: '0.85em'}}>Registros por página:</span>
                              <select 
                                className="form-select form-select-sm" 
                                style={{ width: "70px" }}
                                value={pageSize}
                                onChange={(e) => {
                                  const newSize = Number(e.target.value);
                                  setPageSize(newSize);
                                  setPageIndex(0);
                                }}
                              >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                              </select>
                            </div>
                            <div className="text-muted" style={{fontSize: '0.85em'}}>
                              Mostrando {filteredDataCount > 0 ? pageIndex * pageSize + 1 : 0} a {Math.min((pageIndex + 1) * pageSize, filteredDataCount)} de {filteredDataCount} registros{globalFilter && ` (filtrados de ${facturacion?.detalles?.length || 0} totales)`}
                            </div>
                            <div>
                              <ul className="pagination mb-0">
                                {/* Primera página */}
                                <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setPageIndex(0)}
                                    disabled={pageIndex === 0}
                                  >
                                    &laquo;
                                  </button>
                                </li>
                                
                                {/* Página anterior */}
                                <li className={`page-item ${pageIndex === 0 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setPageIndex(pageIndex - 1)}
                                    disabled={pageIndex === 0}
                                  >
                                    &lt;
                                  </button>
                                </li>
                                
                                {/* Números de página */}
                                {(() => {
                                  const totalPages = Math.ceil(filteredDataCount / pageSize);
                                  const maxPagesToShow = 5;
                                  let startPage = Math.max(0, pageIndex - Math.floor(maxPagesToShow / 2));
                                  let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
                                  
                                  if (endPage - startPage + 1 < maxPagesToShow) {
                                    startPage = Math.max(0, endPage - maxPagesToShow + 1);
                                  }
                                  
                                  const pages = [];
                                  for (let i = startPage; i <= endPage; i++) {
                                    pages.push(
                                      <li key={i} className={`page-item ${pageIndex === i ? 'active' : ''}`}>
                                        <button 
                                          className="page-link" 
                                          onClick={() => setPageIndex(i)}
                                        >
                                          {i + 1}
                                        </button>
                                      </li>
                                    );
                                  }
                                  return pages;
                                })()}
                                
                                {/* Página siguiente */}
                                <li className={`page-item ${pageIndex === Math.ceil((facturacion?.detalles?.length || 0) / pageSize) - 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setPageIndex(pageIndex + 1)}
                                    disabled={pageIndex === Math.ceil((facturacion?.detalles?.length || 0) / pageSize) - 1}
                                  >
                                    &gt;
                                  </button>
                                </li>
                                
                                {/* Última página */}
                                <li className={`page-item ${pageIndex === Math.ceil((facturacion?.detalles?.length || 0) / pageSize) - 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => setPageIndex(Math.ceil((facturacion?.detalles?.length || 0) / pageSize) - 1)}
                                    disabled={pageIndex === Math.ceil((facturacion?.detalles?.length || 0) / pageSize) - 1}
                                  >
                                    &raquo;
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center my-5">
                          <p>No hay datos disponibles</p>
                          <pre>{JSON.stringify(facturacion, null, 2)}</pre>{" "}
                          {/* Para debug */}
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal para visualizar archivos */}
        <FileViewer
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          fileUrl={currentFile.url}
          fileName={currentFile.name}
          fileType={currentFile.type}
        />
      </div>
    </React.Fragment>
  );
};

export default ReporteFacturacion;
